import { useState, useEffect } from 'react';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { AdminHeader, LoadingGrid, EmptyState, ItemCard, FormActions } from '@/components/admin/AdminComponents';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera } from 'lucide-react';

const initialForm = {
  name: '', short_description: '', main_image: '', key_features: '',
  typical_applications: '', spec_summary: '', status: 'Active'
};

const AdminCameraModules = () => {
  const { items, loading, isDialogOpen, editingItem, setIsDialogOpen, createItem, updateItem, deleteItem, openCreate, openEdit, closeDialog } = useAdminCrud('camera-modules', 'Camera module');
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name, short_description: editingItem.short_description,
        main_image: editingItem.main_image || '', key_features: editingItem.key_features || '',
        typical_applications: editingItem.typical_applications || '', spec_summary: editingItem.spec_summary || '',
        status: editingItem.status
      });
    } else {
      setFormData(initialForm);
    }
  }, [editingItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = editingItem ? await updateItem(editingItem.id, formData) : await createItem(formData);
    if (success) setFormData(initialForm);
  };

  const update = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const updateSelect = (field) => (v) => setFormData(prev => ({ ...prev, [field]: v }));

  return (
    <div data-testid="admin-camera-modules">
      <AdminHeader
        title="Camera Modules" subtitle="Manage camera module products" addLabel="Add Module"
        isDialogOpen={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { closeDialog(); setFormData(initialForm); } }}
        dialogTitle={editingItem ? 'Edit Camera Module' : 'Add Camera Module'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={update('name')} required placeholder="e.g., CM-200 Series" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={updateSelect('status')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Legacy">Legacy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Short Description *</Label>
            <Textarea value={formData.short_description} onChange={update('short_description')} required rows={2} placeholder="Brief product description" />
          </div>
          <div className="space-y-2">
            <Label>Main Image URL</Label>
            <Input value={formData.main_image} onChange={update('main_image')} placeholder="https://example.com/image.jpg" />
          </div>
          <div className="space-y-2">
            <Label>Key Features (one per line)</Label>
            <Textarea value={formData.key_features} onChange={update('key_features')} rows={4} placeholder="Sony STARVIS sensor&#10;120dB HDR&#10;IP67 rated" />
          </div>
          <div className="space-y-2">
            <Label>Typical Applications</Label>
            <Textarea value={formData.typical_applications} onChange={update('typical_applications')} rows={2} placeholder="Fleet vehicles, buses, trucks" />
          </div>
          <div className="space-y-2">
            <Label>Spec Summary</Label>
            <Textarea value={formData.spec_summary} onChange={update('spec_summary')} rows={3} placeholder="Technical specifications" />
          </div>
          <FormActions onCancel={closeDialog} isEditing={!!editingItem} />
        </form>
      </AdminHeader>

      {loading ? <LoadingGrid count={3} /> : items.length === 0 ? (
        <EmptyState Icon={Camera} title="No camera modules yet" subtitle="Add your first camera module to display on the website" onAdd={openCreate} addLabel="Add Module" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <ItemCard key={item.id} item={item} titleField="name" descField="short_description" imageField="main_image" statusField="status" Icon={Camera} onEdit={openEdit} onDelete={deleteItem} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCameraModules;
